import React, { useEffect, useLayoutEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SuccessHeroBit from '../components/success/SuccessHeroBit';
import OrderCard from '../components/success/OrderCard';
import BrandMessage from '../components/success/BrandMessage';
import NextSteps from '../components/success/NextSteps';
import LuxuryLoading from '../components/ui/LuxuryLoading';
import { FaArrowLeft, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import '../styles/globals.css';
import '../styles/checkout.css';
import { checkoutPageTransition } from "../components/animations/checkoutAnimations";
import { OrderService } from '../api/order.api';

const OrderSuccess = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [attempts, setAttempts] = useState(0);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const fetchOrderData = async () => {
            if (!location.state?.orderId) {
                setError('No order ID found');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await OrderService.getOrderById(location.state.orderId);

                if (response.success) {
                    const order = response.data;

                    // Validate payment status
                    if (order.paymentStatus === 'pending' || order.paymentStatus === 'processing') {
                        // If still pending, retry after delay (max 3 retries)
                        if (attempts < 3) {
                            setAttempts(prev => prev + 1);
                            setTimeout(() => {
                                fetchOrderData();
                            }, 2000); // Retry after 2 seconds
                            return;
                        }
                    }

                    // Merge location state with fresh backend data
                    const mergedData = {
                        ...location.state,
                        ...order,
                        items: order.orderItems || location.state.items || [],
                        address: order.shippingAddress || location.state.address || {},
                        paymentMethod: order.paymentMethod?.id || location.state.paymentMethod || 'razorpay',
                        total: order.total || location.state.total || 0,
                        estimatedDelivery: location.state.estimatedDelivery || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                        orderNumber: order.orderNumber || location.state.orderNumber,
                        orderStatus: order.orderStatus || location.state.orderStatus || 'confirmed',
                        paymentStatus: order.paymentStatus || location.state.paymentStatus || 'completed'
                    };

                    setOrderData(mergedData);
                    setError('');

                    // If payment status is still pending after retries, show warning
                    if (order.paymentStatus === 'pending' || order.paymentStatus === 'processing') {
                        setError('Payment is still processing. This may take a few moments...');
                    }

                } else {

                    // Use location state as fallback
                    if (location.state) {
                        setOrderData(location.state);
                    }
                }
            } catch (err) {

                // Use location state as fallback
                if (location.state) {
                    setOrderData(location.state);
                }
            } finally {
                setLoading(false);
            }
        };

        // Start fetching immediately
        fetchOrderData();

        // Cleanup function
        return () => {
            // Clear any pending timeouts
        };
    }, [location.state, navigate, attempts]);

    // If no state and no loading, redirect to home
    useEffect(() => {
        if (!loading && !orderData && !location.state) {
            navigate('/');
        }
    }, [loading, orderData, location.state, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-pearl to-nude/10 flex items-center justify-center">
                <LuxuryLoading
                    size="xl"
                    message={attempts > 0 ? `Verifying payment... (Attempt ${attempts}/3)` : "Loading your order details..."}
                    showSpinner={true}
                    variant="elegant"
                />
            </div>
        );
    }

    if (error && !orderData) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-pearl to-nude/10 flex items-center justify-center">
                <div className="text-center max-w-md px-4">
                    <div className="w-20 h-20 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-6">
                        <FaExclamationTriangle className="w-10 h-10 text-error" />
                    </div>
                    <h2 className="text-2xl font-medium text-charcoal mb-3">Unable to Load Order</h2>
                    <p className="text-charcoal/60 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="px-6 py-3 rounded-full bg-champagne text-white hover:bg-champagne/80 transition-colors"
                    >
                        Go to Home
                    </button>
                </div>
            </div>
        );
    }

    if (!orderData) {
        return null;
    }

    const {
        orderId,
        orderNumber,
        items = [],
        address = {},
        paymentMethod,
        total = 0,
        estimatedDelivery,
        orderStatus,
        paymentStatus
    } = orderData;

    return (
        <motion.div
            variants={checkoutPageTransition}
            initial="initial"
            animate="animate"
            exit="exit"
            className="min-h-screen bg-gradient-to-b from-pearl to-nude/10"
        >
            <Navbar />

            {/* Error/Warning Banner */}
            {error && (
                <div className="bg-amber-50 border border-amber-200">
                    <div className="container-luxury max-w-6xl mx-auto px-4 py-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <FaExclamationTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                                <p className="text-sm text-amber-800">{error}</p>
                            </div>
                            <button
                                onClick={() => setError('')}
                                className="text-amber-600 hover:text-amber-800"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <main className="container-luxury max-w-6xl mx-auto px-4 py-8 md:py-12">
                {/* Back Button */}
                <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    onClick={() => navigate('/shop')}
                    className="flex items-center gap-2 text-sm text-charcoal/60 hover:text-champagne transition-colors mb-8"
                >
                    <FaArrowLeft className="w-4 h-4" />
                    Continue Shopping
                </motion.button>

                <SuccessHeroBit
                    orderId={orderId}
                    orderNumber={orderNumber}
                    paymentStatus={paymentStatus}
                />

                {/* Payment Status Indicator */}
                <div className="mb-8">
                    <div className={`p-4 rounded-xl ${
                        paymentStatus === 'completed' || paymentStatus === 'paid'
                            ? 'bg-sage/10 border border-sage/30'
                            : 'bg-amber-50 border border-amber-200'
                    }`}>
                        <div className="flex items-center gap-3">
                            {paymentStatus === 'completed' || paymentStatus === 'paid' ? (
                                <FaCheckCircle className="w-5 h-5 text-sage flex-shrink-0" />
                            ) : (
                                <FaExclamationTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                            )}
                            <div>
                                <p className="font-medium text-charcoal">
                                    Payment Status: <span className={
                                    paymentStatus === 'completed' || paymentStatus === 'paid'
                                        ? 'text-sage'
                                        : 'text-amber-600'
                                }>
                                        {paymentStatus === 'completed' || paymentStatus === 'paid'
                                            ? 'Successful'
                                            : paymentStatus}
                                    </span>
                                </p>
                                <p className="text-sm text-charcoal/60">
                                    Order Status: {orderStatus || 'Processing'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
                    {/* Left: Order Details */}
                    <div className="lg:col-span-2 space-y-8">
                        <OrderCard
                            orderId={orderId}
                            orderNumber={orderNumber}
                            items={items}
                            address={address}
                            paymentMethod={paymentMethod}
                            total={total}
                            estimatedDelivery={estimatedDelivery}
                            orderStatus={orderStatus}
                            paymentStatus={paymentStatus}
                        />

                        <BrandMessage />
                    </div>

                    {/* Right: Next Steps */}
                    <div className="lg:col-span-1">
                        <NextSteps
                            orderId={orderId}
                            orderNumber={orderNumber}
                            paymentStatus={paymentStatus}
                        />
                    </div>
                </div>

                {/* Order Timeline - Dynamic based on status */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="mt-12 pt-12 border-t border-nude/30"
                >
                    <h3 className="font-heading text-2xl font-medium text-charcoal mb-8 text-center">
                        Your Order Journey
                    </h3>

                    <div className="flex flex-col md:flex-row justify-between items-center relative">
                        {/* Timeline Line */}
                        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-nude/30 hidden md:block"></div>

                        {[
                            {
                                step: 'Order Placed',
                                status: paymentStatus === 'completed' ? 'complete' : 'current',
                                date: 'Today'
                            },
                            {
                                step: 'Payment',
                                status: paymentStatus === 'completed' ? 'complete' : 'current',
                                date: 'Today'
                            },
                            {
                                step: 'Order Confirmed',
                                status: (paymentStatus === 'completed' && orderStatus === 'confirmed') ? 'complete' : 'current',
                                date: 'Today'
                            },
                            {
                                step: 'Processing',
                                status: (orderStatus === 'confirmed') ? 'complete' : 'upcoming',
                                date: '1-2 days'
                            },
                            {
                                step: 'Delivered',
                                status: 'upcoming',
                                date: '4-7 days'
                            }
                        ].map((step, index) => (
                            <motion.div
                                key={step.step}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                                className="flex flex-col items-center mb-8 md:mb-0 relative z-10"
                            >
                                <div className={`
                                    w-12 h-12 rounded-full flex items-center justify-center mb-3
                                    ${step.status === 'complete' ? 'bg-gradient-to-br from-sage to-sage/80 text-white' :
                                    step.status === 'current' ? 'bg-gradient-to-br from-champagne to-champagne/80 text-white animate-pulse' :
                                        'bg-nude/30 text-charcoal/30'}
                                `}>
                                    {step.status === 'complete' ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : index + 1}
                                </div>

                                <div className="text-center">
                                    <p className="font-medium text-charcoal text-sm mb-1">{step.step}</p>
                                    <p className="text-xs text-charcoal/50">{step.date}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Refresh Button for pending payments */}
                {(paymentStatus === 'pending' || paymentStatus === 'processing') && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-8 text-center"
                    >
                        <button
                            onClick={() => {
                                setLoading(true);
                                setAttempts(0);
                                // Trigger a refetch
                                setTimeout(() => {
                                    setLoading(false);
                                }, 1000);
                            }}
                            className="px-6 py-3 rounded-full bg-champagne text-white hover:bg-champagne/80 transition-colors"
                        >
                            Refresh Payment Status
                        </button>
                        <p className="text-sm text-charcoal/40 mt-2">
                            Sometimes payments take a few moments to process
                        </p>
                    </motion.div>
                )}

                {/* Final CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="mt-12 text-center"
                >
                    <p className="text-charcoal/60 mb-6">
                        Thank you for trusting KROMAIC with your beauty ritual.<br />
                        We're honored to be part of your journey.
                    </p>
                    <button
                        onClick={() => navigate('/account/orders')}
                        className="px-8 py-3 rounded-full border border-champagne text-champagne hover:bg-champagne hover:text-white transition-colors"
                    >
                        View Order History
                    </button>
                </motion.div>
            </main>

            <Footer />
        </motion.div>
    );
};

export default OrderSuccess;